/**
 * MATELI CUSTOM MEDIA GALLERY
 * ============================
 * Custom version of Shopify Dawn's media-gallery component with enhanced features:
 * 1. Thumbnail prev/next navigation support
 * 2. Video snap handling for auto-play on scroll
 * 3. Smart vertical scroll prevention (container-aware)
 * 4. Comprehensive debug logging
 * 
 * This file extends Dawn's default media gallery functionality without breaking
 * existing features. All custom code is marked with "CUSTOM:" comments.
 */

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    /**
     * CONSTRUCTOR
     * Initializes the media gallery component when it's added to the DOM
     * Sets up all event listeners and custom features
     */
    constructor() {
      super();
      
      // ===== DEBUG LOGGING SETUP =====
      // Set DEBUG to false in production to disable console logs
      this.DEBUG = false;
      this.log = (...args) => this.DEBUG && console.log('[MEDIA GALLERY]', ...args);
      
      this.log('🚀 Initializing media gallery...');
      
      // ===== FIND DOM ELEMENTS =====
      // Store references to key elements for easy access throughout the component
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),    // Accessibility announcements
        viewer: this.querySelector('[id^="GalleryViewer"]'),        // Main image/video gallery
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]') // Thumbnail navigation strip
      }
      
      this.log('Elements found:', {
        liveRegion: !!this.elements.liveRegion,
        viewer: !!this.elements.viewer,
        thumbnails: !!this.elements.thumbnails
      });
      
      // ===== PREVENT INTERFERENCE FROM OTHER SCRIPTS =====
      // Track when setActiveMedia was last called to ignore interfering slideChanged events
      this.lastSetActiveMediaTime = 0;
      
      // ===== MEDIA QUERY FOR RESPONSIVE BEHAVIOR =====
      // Check if we're on desktop (750px+) for layout decisions
      this.mql = window.matchMedia('(min-width: 750px)');
      
      // ===== VERTICAL SCROLL PREVENTION =====
      // Run before thumbnail check — this only needs the viewer, not thumbnails.
      // Must apply even for single-image products with tall images.
      this.preventVerticalScroll();

      // Exit early if no thumbnails exist (single image product)
      if (!this.elements.thumbnails) {
        this.log('⚠️ No thumbnails found, skipping thumbnail setup');
        return;
      }

      // ===== DAWN DEFAULT FUNCTIONALITY =====
      // Listen for slide changes in the main viewer (when user swipes/clicks)
      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      
      // Add click handlers to all thumbnail buttons
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      
      // Remove semantic list roles for screen readers on desktop thumbnail layout
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      
      // ===== CUSTOM FEATURES =====
      // Initialize our custom enhancements
      this.initThumbnailNavigation();  // Prev/Next button support
      this.initVideoSnapHandling();    // Auto-play videos on scroll
      this.preventVerticalScroll();    // Smart vertical scroll blocking (container-aware)
      
      this.log('✅ Media gallery initialized successfully');
    }

    /**
     * ON SLIDE CHANGED
     * Called when the main gallery slide changes (user swipes or clicks)
     * Updates the corresponding thumbnail to show which image is active
     * 
     * @param {Event} event - Custom slideChanged event with currentElement details
     */
    onSlideChanged(event) {
      this.log('📍 Slide changed:', event.detail.currentElement.dataset.mediaId);
      
      // SAFEGUARD: Ignore slideChanged if it happens within 300ms of setActiveMedia
      // This prevents interference from global.min.js and other scripts
      const timeSinceSetActive = Date.now() - this.lastSetActiveMediaTime;
      if (timeSinceSetActive < 300) {
        this.log(`⚠️ Ignoring slideChanged - too soon after setActiveMedia (${timeSinceSetActive}ms)`);
        return;
      }
      
      // Log stack trace to see what triggered this
      if (this.DEBUG) {
        console.trace('[MEDIA GALLERY] slideChanged stack trace:');
      }
      
      // Find the thumbnail that corresponds to the new active slide
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    /**
     * SET ACTIVE MEDIA
     * Core function that changes which image/video is displayed in the main gallery
     * Also updates thumbnails and handles scrolling
     * 
     * @param {string} mediaId - The data-media-id of the media to activate
     * @param {boolean} prepend - If true, move this media to the start of the gallery
     */
    setActiveMedia(mediaId, prepend) {
      this.log(`🎯 setActiveMedia called: mediaId=${mediaId}, prepend=${prepend}`);
      
      // Record timestamp to ignore interfering slideChanged events
      this.lastSetActiveMediaTime = Date.now();
      
      // Find the media element in the main viewer
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      
      if (!activeMedia) {
        this.log('❌ Media not found:', mediaId);
        return;
      }
      
      // Remove 'is-active' class from all media items
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      
      // Add 'is-active' class to the selected media
      activeMedia.classList.add('is-active');
      
      this.log('✅ Active media set:', mediaId);

      // If prepend is true, move this media to the beginning (variant change behavior)
      if (prepend) {
        this.log('↗️ Prepending media to start');
        activeMedia.parentElement.prepend(activeMedia);
        
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        
        // Reset slider pagination if using slider component
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      // Prevent sticky header from revealing during scroll
      this.preventStickyHeader();
      
      // Scroll the gallery to show the active media
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      
      // Start playing if it's a video
      this.playActiveMedia(activeMedia);

      // Update thumbnail highlighting and accessibility announcements
      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    /**
     * SET ACTIVE THUMBNAIL
     * Highlights the active thumbnail and scrolls it into view if needed
     * 
     * @param {HTMLElement} thumbnail - The thumbnail element to activate
     */
    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.log('🖼️ Setting active thumbnail');
      
      // Remove aria-current from all thumbnails
      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      
      // Set aria-current on the active thumbnail (for screen readers)
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      
      // If thumbnail is already visible, no need to scroll
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      // Scroll thumbnail strip to show the active thumbnail
      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    /**
     * ANNOUNCE LIVE REGION
     * Accessibility feature: announces to screen readers when image changes
     * 
     * @param {HTMLElement} activeItem - The active media element
     * @param {string} position - The position number to announce (e.g., "1 of 5")
     */
    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      
      // Wait for image to load before announcing
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        
        // Hide announcement after 2 seconds
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      
      // Trigger onload by setting src (even if already set)
      image.src = image.src;
    }

    /**
     * PLAY ACTIVE MEDIA
     * Pauses all other media and plays the active one (if it's a video)
     * 
     * @param {HTMLElement} activeItem - The media item to play
     */
    playActiveMedia(activeItem) {
      this.log('▶️ Playing active media');
      
      // Pause all videos/models on the page
      window.pauseAllMedia();
      
      // Load and play the active media if it's deferred (video/model)
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    /**
     * PREVENT STICKY HEADER
     * Tells the sticky header not to reveal itself during gallery scrolling
     * Improves UX by keeping header hidden while browsing images
     */
    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    /**
     * REMOVE LIST SEMANTIC
     * Removes ARIA list roles from slider for better screen reader experience
     * Only applies on desktop with thumbnail layout
     */
    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }

    /**
     * ========================================
     * CUSTOM: INITIALIZE THUMBNAIL NAVIGATION
     * ========================================
     * Adds functionality to prev/next buttons in thumbnail strip
     * 
     * STRATEGY: Instead of calling setActiveMedia (which fights with slideChanged),
     * we directly scroll the main gallery. This triggers slideChanged naturally,
     * which then updates the thumbnail - working WITH the system, not against it.
     */
    initThumbnailNavigation() {
      this.log('🎮 Initializing thumbnail navigation...');
      
      if (!this.elements.thumbnails) return;

      // Find the prev/next buttons in the thumbnail slider
      const prevBtn = this.elements.thumbnails.querySelector('.slider-button--prev');
      const nextBtn = this.elements.thumbnails.querySelector('.slider-button--next');
      
      if (!prevBtn || !nextBtn) {
        this.log('⚠️ Prev/Next buttons not found');
        return;
      }

      // Get all thumbnail elements and main gallery slides
      const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
      const mainGallery = this.elements.viewer.querySelector('[id^="Slider-Gallery-"]');
      
      if (!mainGallery) {
        this.log('⚠️ Main gallery not found');
        return;
      }
      
      const mainSlides = Array.from(mainGallery.querySelectorAll('.product__media-item'));
      
      this.log(`✅ Found ${thumbnails.length} thumbnails and ${mainSlides.length} main slides`);

      /**
       * GET CURRENT INDEX
       * Finds which thumbnail is currently active
       * @returns {number} Index of active thumbnail (0-based)
       */
      const getCurrentIndex = () => {
        const activeThumbnail = this.elements.thumbnails.querySelector('button[aria-current="true"]');
        if (!activeThumbnail) return 0;
        const thumbnailItem = activeThumbnail.closest('[data-target]');
        const index = thumbnails.indexOf(thumbnailItem);
        this.log(`📍 Current thumbnail index: ${index}`);
        return index;
      };

      /**
       * SCROLL TO SLIDE
       * Scrolls the main gallery to show a specific slide
       * This triggers slideChanged naturally, which updates the thumbnail
       * @param {number} index - Index of slide to scroll to
       */
      const scrollToSlide = (index) => {
        if (index < 0 || index >= mainSlides.length) {
          this.log(`❌ Index ${index} out of bounds (0-${mainSlides.length - 1})`);
          return;
        }
        
        const targetSlide = mainSlides[index];
        this.log(`📜 Scrolling main gallery to slide ${index}`);
        
        // Scroll the main gallery - this will trigger slideChanged naturally
        targetSlide.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest', 
          inline: 'start' 
        });
        
        this.log(`✅ Scroll initiated to slide ${index}`);
      };

      /**
       * PREV BUTTON CLICK HANDLER
       * Scrolls to the previous slide
       */
      prevBtn.addEventListener('click', () => {
        this.log('⬅️ PREV button clicked');
        const currentIndex = getCurrentIndex();
        const newIndex = currentIndex - 1;
        this.log(`Prev: current=${currentIndex}, new=${newIndex}`);
        
        if (newIndex >= 0) {
          scrollToSlide(newIndex);
        } else {
          this.log('⚠️ Already at first slide');
        }
      });

      /**
       * NEXT BUTTON CLICK HANDLER
       * Scrolls to the next slide
       */
      nextBtn.addEventListener('click', () => {
        this.log('➡️ NEXT button clicked');
        const currentIndex = getCurrentIndex();
        const newIndex = currentIndex + 1;
        this.log(`Next: current=${currentIndex}, new=${newIndex}`);
        
        if (newIndex < mainSlides.length) {
          scrollToSlide(newIndex);
        } else {
          this.log('⚠️ Already at last slide');
        }
      });
      
      this.log('✅ Thumbnail navigation initialized');
    }

    /**
     * =====================================================
     * CUSTOM: SMART VERTICAL SCROLL PREVENTION
     * =====================================================
     * Container-aware vertical scroll blocking for the
     * product image slider on mobile devices.
     *
     * PROBLEM:
     *   Mobile swipe gestures are rarely a perfect horizontal line.
     *   Without prevention, a slight vertical component during a
     *   horizontal swipe causes the page to scroll instead of
     *   changing slides — making swiping feel broken.
     *
     *   However, when any slide contains a tall image, the parent
     *   <ul class="product__media-list"> stretches to fit it.
     *   If we block vertical scroll on the whole container,
     *   users get stuck and can't scroll past the gallery at all —
     *   even when they're viewing a different, shorter slide.
     *
     * SOLUTION:
     *   Check the <ul> container's rendered dimensions:
     *   - Container height <= width × TALL_RATIO:
     *       All images fit comfortably → block vertical scroll
     *       for clean horizontal swiping. CSS touch-action = "pan-x".
     *   - Container height > width × TALL_RATIO:
     *       At least one tall image is stretching the container →
     *       allow vertical scroll so users can scroll past it.
     *       CSS touch-action = "manipulation".
     *
     * WHY THE CONTAINER (not individual images)?
     *   The <ul> is the scroll-snap container. Its height is set
     *   by the tallest child slide. Even if you're currently on
     *   a square slide, the container is still tall — so the
     *   touch-action must match the container's actual size.
     *
     * @see TALL_RATIO — the threshold constant (default 1.2)
     */
    preventVerticalScroll() {
      this.log('🚫 Setting up smart vertical scroll prevention...');

      // ── Find the main gallery slider element (<ul>) ──
      const mainSlider = this.elements.viewer?.querySelector('[id^="Slider-Gallery-"]');
      if (!mainSlider) {
        this.log('⚠️ No main slider found — skipping vertical scroll prevention');
        return;
      }

      // ── Configuration ──
      // If the container's height/width ratio exceeds this, it's "tall"
      // and vertical scrolling will be allowed
      const TALL_RATIO = 1.1;

      /**
       * IS CONTAINER TALL
       * Checks the rendered dimensions of the <ul> container.
       * The <ul> stretches to the tallest image across ALL slides,
       * so this single check covers every slide at once.
       *
       * @returns {boolean} true if container height/width > TALL_RATIO
       */
      const isContainerTall = () => {
        const containerW = mainSlider.clientWidth;
        const containerH = mainSlider.clientHeight;

        if (!containerW || !containerH) {
          this.log('📐 Container dimensions not available — defaulting to NOT tall');
          return false;
        }

        const ratio = containerH / containerW;
        const isTall = ratio > TALL_RATIO;
        this.log(`📐 Container ratio: ${ratio.toFixed(2)} (${containerW}×${containerH}px) → ${isTall ? 'TALL — vertical scroll ALLOWED' : 'COMPACT — vertical scroll BLOCKED'}`);
        return isTall;
      };

      /**
       * UPDATE TOUCH ACTION CSS
       * Dynamically switches the CSS touch-action property on the slider:
       *   - "pan-x"        → only horizontal gestures (blocks vertical scroll)
       *   - "manipulation"  → both directions allowed (permits vertical scroll)
       */
      const updateTouchAction = () => {
        const tall = isContainerTall();
        const newAction = tall ? 'manipulation' : 'pan-x';
        mainSlider.style.touchAction = newAction;
        this.log(`🖐️ touch-action updated → "${newAction}"`);
      };

      // ── Cache the tall state to avoid recalculating on every touchmove ──
      let cachedIsTall = false;

      const refreshCache = () => {
        cachedIsTall = isContainerTall();
      };

      // ── Touch event tracking state ──
      let touchStartX = 0;
      let touchStartY = 0;
      let isTouching = false;

      /**
       * TOUCH START
       * Records the initial finger position when user starts touching the slider.
       * Also refreshes the cached tall state for this gesture.
       */
      mainSlider.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        isTouching = true;
        refreshCache(); // re-evaluate once per gesture, not per move
        this.log(`👆 Touch start at (${touchStartX.toFixed(0)}, ${touchStartY.toFixed(0)}) | container tall: ${cachedIsTall}`);
      }, { passive: true });

      /**
       * TOUCH MOVE
       * JS-level backup for vertical scroll prevention.
       * Compares horizontal vs vertical movement delta:
       *   - If container is tall → let the browser handle it naturally
       *   - If container is compact AND vertical > horizontal → block vertical
       */
      mainSlider.addEventListener('touchmove', (e) => {
        if (!isTouching) return;

        // Allow vertical scroll if container is tall — no intervention needed
        if (cachedIsTall) return;

        const deltaX = Math.abs(e.touches[0].clientX - touchStartX);
        const deltaY = Math.abs(e.touches[0].clientY - touchStartY);

        // Only block if vertical movement dominates and exceeds 10px threshold
        if (deltaY > deltaX && deltaY > 10) {
          e.preventDefault();
          this.log(`⛔ Vertical scroll BLOCKED (ΔY: ${deltaY.toFixed(0)}px > ΔX: ${deltaX.toFixed(0)}px)`);
        }
      }, { passive: false }); // passive: false is required to allow preventDefault()

      /**
       * TOUCH END
       * Resets touch tracking state when finger is lifted.
       */
      mainSlider.addEventListener('touchend', () => {
        isTouching = false;
        this.log('👆 Touch end — tracking reset');
      }, { passive: true });

      // ── Re-evaluate touch-action when slide changes ──
      // Container dimensions don't change per slide, but re-check in case
      // of dynamic content changes (variant switches, lazy-loaded images)
      mainSlider.addEventListener('scroll', debounce(() => {
        this.log('📜 Slide scroll detected — re-evaluating touch-action...');
        updateTouchAction();
      }, 150), { passive: true });

      // ── Initial evaluation ──
      // Run after page load to set correct touch-action for the container
      window.addEventListener('load', () => {
        this.log('🔄 Page loaded — setting initial touch-action');
        updateTouchAction();
      }, { once: true });

      // Fallback: also run after 1s for lazily-loaded images that may resize the container
      setTimeout(() => {
        this.log('🔄 Delayed init — re-checking touch-action for lazy images');
        updateTouchAction();
      }, 1000);

      this.log('✅ Smart vertical scroll prevention setup complete');
    }

    /**
     * ====================================
     * CUSTOM: INITIALIZE VIDEO SNAP HANDLING
     * ====================================
     * Automatically builds and plays videos when they scroll into view
     * This improves UX by showing videos immediately without requiring a click
     * 
     * Features:
     * - Detects which slide is centered in viewport (snap point)
     * - Builds video element from template if needed
     * - Auto-plays video when it becomes active
     * - Pauses other videos to save bandwidth
     */
    initVideoSnapHandling() {
      this.log('🎬 Initializing video snap handling...');
      
      // Separate debug flag for video-specific logs (set to true to debug videos)
      const DEBUG_VIDEO = false;
      const logVideo = (...args) => DEBUG_VIDEO && console.log('[VIDEO SNAP]', ...args);

      /**
       * DECODE HTML
       * Converts HTML entities back to regular characters
       * Needed because Shopify sometimes escapes template content
       * @param {string} html - HTML string with entities
       * @returns {string} Decoded HTML
       */
      const decodeHtml = (html) => {
        const t = document.createElement('textarea');
        t.innerHTML = html;
        return t.value;
      };

      /**
       * GET GALLERY
       * Finds the main gallery <ul> element
       * @returns {HTMLElement|null} The gallery element
       */
      const getGallery = () => {
        return this.elements.viewer?.querySelector('[id^="Slider-Gallery-"]') || 
               this.elements.viewer?.querySelector('.product__media-list');
      };

      /**
       * GET SNAP ACTIVE SLIDE
       * Determines which slide is currently centered in the viewport
       * Uses geometric calculation to find the slide closest to center
       * 
       * @param {HTMLElement} scroller - The scrolling container
       * @returns {HTMLElement|null} The centered slide element
       */
      const getSnapActiveSlide = (scroller) => {
        const slides = Array.from(scroller.querySelectorAll('li.product__media-item, li.slider__slide'));
        if (!slides.length) return null;

        // Get the center point of the scroller viewport
        const r = scroller.getBoundingClientRect();
        const centerX = r.left + r.width / 2;

        // Find the slide whose center is closest to viewport center
        let best = null, bestDist = Infinity;
        for (const li of slides) {
          const lr = li.getBoundingClientRect();
          if (lr.width < 5) continue; // Skip hidden slides
          
          const cx = lr.left + lr.width / 2; // Slide center X
          const d = Math.abs(cx - centerX);  // Distance from viewport center
          
          if (d < bestDist) { 
            bestDist = d; 
            best = li; 
          }
        }
        return best;
      };

      /**
       * PAUSE OTHER VIDEOS
       * Pauses all videos except the one in the active slide
       * Saves bandwidth and prevents audio conflicts
       * 
       * @param {HTMLElement} scroller - The gallery container
       * @param {HTMLElement} activeLi - The active slide (don't pause this one)
       */
      const pauseOtherVideos = (scroller, activeLi) => {
        scroller.querySelectorAll('video').forEach(v => {
          const li = v.closest('li');
          if (li && li !== activeLi) {
            try { v.pause(); } catch(e) {}
          }
        });
      };

      /**
       * EXTRACT MP4 URL
       * Extracts the video URL from various possible sources:
       * 1. Existing <video> element
       * 2. <template> tag (Shopify deferred media pattern)
       * 3. data-src attribute
       * 
       * @param {HTMLElement} dm - The deferred-media element
       * @returns {string|null} The MP4 URL or null if not found
       */
      const extractMp4Url = (dm) => {
        // Check if video already exists
        const existing = dm.querySelector('video source[src]');
        if (existing?.getAttribute('src')) return existing.getAttribute('src');

        // Check template tag (Dawn's deferred media pattern)
        const tpl = dm.querySelector('template');
        const raw = (tpl?.innerHTML || '').trim();
        if (raw) {
          // Decode HTML entities if present
          const decoded = (raw.includes('&lt;') || raw.includes('&gt;')) ? decodeHtml(raw) : raw;
          
          // Try data-src first (common pattern)
          let m = decoded.match(/data-src\s*=\s*["']([^"']+)["']/i);
          if (m?.[1]) return m[1];
          
          // Fallback to src attribute
          m = decoded.match(/src\s*=\s*["']([^"']+\.mp4[^"']*?)["']/i);
          if (m?.[1]) return m[1];
        }

        // Last resort: check for data-src in DOM
        const domData = dm.querySelector('source[data-src]');
        if (domData?.getAttribute('data-src')) return domData.getAttribute('data-src');

        return null;
      };

      /**
       * ENSURE VIDEO NODE
       * Creates a <video> element if it doesn't exist yet
       * Builds it from the MP4 URL and poster image
       * 
       * @param {HTMLElement} dm - The deferred-media container
       * @returns {HTMLVideoElement|null} The video element or null if no MP4 found
       */
      const ensureVideoNode = (dm) => {
        // Return existing video if already built
        const existingVideo = dm.querySelector('video');
        if (existingVideo) return existingVideo;

        // Get the MP4 URL
        const mp4 = extractMp4Url(dm);
        if (!mp4) return null;

        // Ensure URL has protocol
        const src = mp4.startsWith('//') ? ('https:' + mp4) : mp4;
        
        // Get poster image and accessibility label
        const posterImg = dm.querySelector('button.deferred-media__poster img') || dm.querySelector('img');
        const poster = posterImg?.getAttribute('src') || '';
        const aria = dm.getAttribute('aria-label') || posterImg?.getAttribute('alt') || '';

        // Create video element with proper attributes
        const v = document.createElement('video');
        v.playsInline = true;
        v.setAttribute('playsinline', '');
        v.muted = true;
        v.setAttribute('muted', '');
        v.controls = true;
        v.preload = 'none';
        if (aria) v.setAttribute('aria-label', aria);
        if (poster) v.setAttribute('poster', poster);

        // Add source element
        const s = document.createElement('source');
        s.type = 'video/mp4';
        s.src = src;
        v.appendChild(s);
        dm.appendChild(v);

        // Mark as loaded and hide the poster button
        dm.setAttribute('loaded', 'true');
        const btn = dm.querySelector('button.deferred-media__poster');
        if (btn) btn.style.display = 'none';

        return v;
      };

      /**
       * TRY AUTO PLAY
       * Attempts to auto-play a video
       * May fail on mobile without user gesture, but that's okay
       * 
       * @param {HTMLVideoElement} video - The video to play
       */
      const tryAutoPlay = (video) => {
        if (!video) return;
        
        try { video.load(); } catch(e) {}
        
        const p = video.play();
        if (p?.catch) p.catch(err => logVideo('Autoplay blocked:', err));
      };

      /**
       * HANDLE ACTIVE
       * Main handler called when active slide changes
       * Pauses other videos and plays the active one
       * 
       * @param {HTMLElement} scroller - The gallery container
       */
      const handleActive = (scroller) => {
        // Find which slide is centered
        const active = getSnapActiveSlide(scroller);
        if (!active) return;

        // Pause all other videos
        pauseOtherVideos(scroller, active);

        // Check if this slide has a video
        const dm = active.querySelector('deferred-media');
        if (!dm) return;

        logVideo('Active slide (snap) -> build + play', { active, dm });

        // Build video element if needed
        const v = ensureVideoNode(dm);
        if (!v) {
          logVideo('❌ Could not build video (no mp4 found)');
          return;
        }

        // Try to auto-play
        tryAutoPlay(v);
      };

      /**
       * BIND
       * Sets up scroll listeners and mutation observers
       * Called once when the page loads
       */
      const bind = () => {
        const ul = getGallery();
        if (!ul) {
          logVideo('No gallery <ul> found');
          return;
        }

        // Prevent double-binding
        if (ul.dataset.galleryVideoSnapBound === 'true') return;
        ul.dataset.galleryVideoSnapBound = 'true';
        logVideo('Bound ok', ul);

        // Handle initial state
        handleActive(ul);

        // Handle scroll events (debounced)
        let t = null;
        ul.addEventListener('scroll', () => {
          if (t) clearTimeout(t);
          t = setTimeout(() => handleActive(ul), 140);
        }, { passive: true });

        // Watch for DOM changes (variant switches, etc.)
        if ('MutationObserver' in window) {
          const mo = new MutationObserver(() => {
            handleActive(ul);
          });
          mo.observe(ul, { childList: true, subtree: true });
        }
      };

      // Initialize when page is ready
      if (document.readyState === 'complete') bind();
      else window.addEventListener('load', bind, { once: true });
      
      this.log('✅ Video snap handling initialized');
    }
  });
}
